import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    CloseButton,
    Dialog,
    HStack,
    IconButton,
    Portal,
    Text,
    Textarea,
    VStack,
} from "@chakra-ui/react";
import { HiPaperClip, HiXMark } from "react-icons/hi2";
import { Field, FormInput } from "@/components/jobOffer/ui";

const TAG_MAP = [
    { api: "{{firstname}}",   pl: "{{imie}}",       en: "{{firstname}}",   de: "{{vorname}}"  },
    { api: "{{lastname}}",    pl: "{{nazwisko}}",   en: "{{lastname}}",    de: "{{nachname}}" },
    { api: "{{jobTitle}}",    pl: "{{stanowisko}}", en: "{{jobTitle}}",    de: "{{stelle}}"   },
    { api: "{{companyName}}", pl: "{{firma}}",      en: "{{companyName}}", de: "{{firma}}"    },
];

const normalizeBody = (text) => {
    let result = text;
    for (const entry of TAG_MAP) {
        for (const [key, display] of Object.entries(entry)) {
            if (key !== "api" && display !== entry.api) {
                result = result.replaceAll(display, entry.api);
            }
        }
    }
    return result;
};

const BulkEmailModal = ({ isOpen, onClose, count, onSubmit, isLoading, title }) => {
    const { t, i18n } = useTranslation("models");
    const displayTags = TAG_MAP.map(e => e[i18n.language] ?? e.en);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [files, setFiles] = useState([]);
    const bodyRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setSubject("");
            setBody("");
            setFiles([]);
        }
    }, [isOpen]);

    const insertTag = (tag) => {
        const el = bodyRef.current;
        if (!el) {
            setBody(prev => prev + tag);
            return;
        }
        const start = el.selectionStart ?? body.length;
        const end = el.selectionEnd ?? body.length;
        const next = body.slice(0, start) + tag + body.slice(end);
        setBody(next);
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + tag.length, start + tag.length);
        });
    };

    const handleSubmit = () => {
        onSubmit(subject.trim(), normalizeBody(body.trim()), files);
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={(e) => { if (!e.open) onClose(); }}>
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner p={4}>
                    <Dialog.Content
                        bg="white"
                        color="gray.900"
                        borderColor="gray.200"
                        border="1px solid"
                        boxShadow="0 4px 24px rgba(23,21,31,0.12)"
                        w={{ base: "calc(100vw - 2rem)", md: "2xl" }}
                        maxW="100%"
                        maxH="calc(100dvh - 2rem)"
                        overflowY="auto"
                        borderRadius={12}
                    >
                        <Dialog.Body pt={6} pb={6} px={6}>
                            <Text fontWeight={700} fontSize="lg" mb={4} color="gray.900">
                                {title ?? t("candidatePage.bulkEmailModalTitle", { count })}
                            </Text>
                            <VStack align="stretch" gap={4}>
                                <Field label={t("candidatePage.bulkEmailSubject")}>
                                    <FormInput
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                    />
                                </Field>
                                <Box>
                                    <Text fontSize="sm" fontWeight={600} color="gray.800" mb={2}>
                                        {t("candidatePage.bulkEmailBody")}
                                    </Text>
                                    {/* Raw Textarea (not FormTextarea) so bodyRef keeps working -- FormTextarea
                                        doesn't forward refs, and insertTag() needs the DOM node for selection. */}
                                    <Textarea
                                        ref={bodyRef}
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        rows={8}
                                        bg="white"
                                        borderColor="gray.200"
                                        color="gray.900"
                                        fontSize="sm"
                                        transition="border-color 0.15s, box-shadow 0.15s"
                                        _placeholder={{ color: "gray.400" }}
                                        _hover={{ borderColor: "gray.300" }}
                                        _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)" }}
                                        resize="vertical"
                                    />
                                    <HStack mt={2} gap={2} flexWrap="wrap">
                                        <Text fontSize="xs" color="gray.500">
                                            {t("candidatePage.bulkEmailTags")}
                                        </Text>
                                        {displayTags.map(tag => (
                                            <Box
                                                key={tag}
                                                as="button"
                                                type="button"
                                                onClick={() => insertTag(tag)}
                                                px={2}
                                                py={0.5}
                                                borderRadius={4}
                                                border="1px solid"
                                                borderColor="gray.200"
                                                bg="gray.50"
                                                color="purple.600"
                                                fontSize="xs"
                                                cursor="pointer"
                                                _hover={{ borderColor: "purple.400", bg: "purple.50" }}
                                                transition="all 0.15s"
                                            >
                                                {tag}
                                            </Box>
                                        ))}
                                    </HStack>
                                </Box>
                            </VStack>
                            <Box mt={4}>
                                <Text fontSize="sm" color="gray.500" mb={2}>
                                    {t("candidateDetailPage.noteAttachmentsLabel")}
                                </Text>
                                <Button
                                    as="label"
                                    htmlFor="email-file-input"
                                    variant="outlined"
                                    size="sm"
                                    cursor="pointer"
                                    color="gray.700"
                                    borderColor="gray.300"
                                    bg="white"
                                    _hover={{ bg: "purple.50", borderColor: "purple.300", color: "purple.700" }}
                                    mb={files.length > 0 ? 3 : 0}
                                >
                                    <HiPaperClip />
                                    {t("candidateDetailPage.noteAttachSelectFiles")}
                                    <input
                                        id="email-file-input"
                                        type="file"
                                        multiple
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            const picked = Array.from(e.target.files ?? []);
                                            setFiles(prev => [...prev, ...picked]);
                                            e.target.value = "";
                                        }}
                                    />
                                </Button>
                                {files.length > 0 && (
                                    <VStack align="stretch" gap={1}>
                                        {files.map((f, i) => (
                                            <HStack
                                                key={i}
                                                bg="gray.50"
                                                border="1px solid"
                                                borderColor="gray.200"
                                                borderRadius={8}
                                                px={3}
                                                py={2}
                                                justify="space-between"
                                            >
                                                <HStack gap={2} minW={0}>
                                                    <Box color="gray.400" flexShrink={0}><HiPaperClip /></Box>
                                                    <Text fontSize="xs" color="gray.700" fontFamily="mono" truncate>{f.name}</Text>
                                                </HStack>
                                                <IconButton
                                                    variant="ghost"
                                                    size="xs"
                                                    aria-label={t("candidateDetailPage.noteRemoveFile")}
                                                    color="gray.400"
                                                    flexShrink={0}
                                                    _hover={{ bg: "red.50", color: "red.500" }}
                                                    onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}
                                                >
                                                    <HiXMark />
                                                </IconButton>
                                            </HStack>
                                        ))}
                                    </VStack>
                                )}
                            </Box>
                            <HStack justify="flex-end" gap={2} mt={6}>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    color="gray.600"
                                    _hover={{ bg: "gray.100" }}
                                    onClick={onClose}
                                    disabled={isLoading}
                                >
                                    {t("candidateDetailPage.changeStatusModalCancel")}
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    loading={isLoading}
                                    disabled={!subject.trim() || !body.trim()}
                                    onClick={handleSubmit}
                                >
                                    {t("candidatePage.bulkEmailSend")}
                                </Button>
                            </HStack>
                        </Dialog.Body>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" position="absolute" top="2" right="2" color="gray.400" _hover={{ bg: "gray.100", color: "gray.600" }} />
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

export default BulkEmailModal;
